import { useState } from 'react';

import { api } from '../lib/api';
import { Alert, Badge, Card, PageHead, formatTime, useLoader } from '../components/ui';

/** Destructive or security-relevant actions read louder than routine ones. */
function actionKind(action: string): string {
  if (action.includes('deleted') || action.includes('revoked')) return 'danger';
  if (action.includes('suspended') || action.includes('flagged')) return 'warn';
  if (action.includes('removed')) return 'warn';
  if (action.includes('verified') || action.includes('created')) return 'ok';
  return 'muted';
}

export function AuditLog() {
  const { data, error, loading } = useLoader(() => api.auditLogs(200));
  const [filter, setFilter] = useState('');

  const rows = (data ?? []).filter((entry) =>
    filter
      ? entry.action.includes(filter) ||
        entry.actor.toLowerCase().includes(filter.toLowerCase())
      : true,
  );

  const actions = Array.from(new Set((data ?? []).map((entry) => entry.action))).sort();

  return (
    <>
      <PageHead
        title="Audit log"
        subtitle="Append-only record of who changed what. Never edited or deleted by the application."
      />

      {error && <Alert kind="error">{error}</Alert>}

      <Card
        tight
        title={`Entries (${(data ?? []).length})`}
        actions={
          <select
            aria-label="Filter entries by action"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            style={{ width: 210 }}
          >
            <option value="">All actions</option>
            {actions.map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </select>
        }
      >
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Target</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => (
                <tr key={entry.id}>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {formatTime(entry.occurred_at)}
                  </td>
                  <td>
                    <Badge kind={actionKind(entry.action)}>{entry.action}</Badge>
                  </td>
                  <td className="mono">{entry.actor}</td>
                  <td className="mono truncate" title={entry.target_id ?? ''}>
                    {entry.target_type ? `${entry.target_type} ${entry.target_id ?? ''}` : '—'}
                  </td>
                  <td className="mono truncate" title={JSON.stringify(entry.metadata ?? {})}>
                    {entry.metadata ? JSON.stringify(entry.metadata) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="empty">{loading ? 'Loading…' : 'No entries match.'}</div>
        )}
      </Card>

      <Card>
        <div className="tile-label">What lands here</div>
        <p className="subtitle" style={{ marginTop: 6 }}>
          API key creation and revocation, domain changes, webhook changes, suppression
          add and remove, and automatic account review flags. An entry attributed to{' '}
          <code>system</code> was written by the platform itself — the complaint-rate check
          is the one that does that today.
        </p>
      </Card>
    </>
  );
}
